import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  FaCarSide,
  FaChevronRight,
  FaFilter,
  FaHeart,
  FaMapMarkerAlt,
  FaRegHeart,
  FaShareAlt,
  FaTachometerAlt,
  FaTimes,
} from 'react-icons/fa';
import RecentlyViewed from './RecentlyViewed';
import Spinner from './Components/Spinner';
import { useAuth } from './AuthContext';
import SaveSearchModal from './SaveSearchModal';
import MarketInsight from './Components/MarketInsight';
import { getVehicleCanonicalUrl, getVehiclePath } from './Utils/vehicleUrls';

const SORT_LABELS = {
  price_asc: 'Price: Low to High',
  price_desc: 'Price: High to Low',
  year_desc: 'Year: Newest',
  year_asc: 'Year: Oldest',
  mileage_asc: 'Mileage: Low to High',
};

function fmtInt(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '0';
  return num.toLocaleString();
}

function fmtPrice(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '$0';
  return `$${num.toLocaleString()}`;
}

function normalizeModel(value) {
  if (!value) return '';
  return value === 'All Models' ? '' : value;
}

function locationLabel(car) {
  return [car?.dealerCity, car?.dealerState].filter(Boolean).join(', ') || 'Location not listed';
}

export function SearchResultCard({
  car,
  saved,
  userId,
  onOpen,
  onToggleSaved,
  onDealerClick,
}) {
  const [isSaved, setIsSaved] = useState(saved);
  const [toast, setToast] = useState('');
  const toastRef = useRef(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setIsSaved(saved);
  }, [saved]);

  useEffect(() => () => {
    if (toastRef.current) window.clearTimeout(toastRef.current);
  }, []);

  const pushToast = (message) => {
    setToast(message);
    if (toastRef.current) window.clearTimeout(toastRef.current);
    toastRef.current = window.setTimeout(() => setToast(''), 1800);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(car);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!userId) {
      pushToast('Sign in to save cars');
      return;
    }

    if (isSaved) {
      const confirmUnsave = window.confirm('Remove this car from your saved list?');
      if (!confirmUnsave) return;
    }

    const result = await onToggleSaved(car.id, isSaved);
    if (!result?.ok && result?.reason !== 'auth') {
      pushToast('Unable to update saved cars');
      return;
    }

    setIsSaved(!isSaved);
    pushToast(isSaved ? 'Removed from saved cars' : 'Saved to your list');
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = getVehicleCanonicalUrl(car, window.location.origin);

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${car.year} ${car.make} ${car.model}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        pushToast('Link copied');
      }
    } catch (err) {
      console.error('Share failed:', err);
      pushToast('Unable to share');
    }
  };

  const title = `${car.year ?? ''} ${car.make ?? ''} ${car.model ?? ''} ${car.trim ?? ''}`.replace(/\s+/g, ' ').trim();
  const mestimateValue = Number(car?.mestimate);
  const mestimateStatus = car?.mestimateStatus || null;
  const mestimateCompsCount = Number(car?.mestimateCompsCount || 0);
  const savingsAmount = Number(car?.savingsAmount);
  const savingsPercent = Number(car?.savingsPercent);
  const differenceAmount = Number(car?.differenceAmount);
  const differencePercent = Number(car?.differencePercent);
  const pricePosition = car?.pricePosition || null;
  const hasSavings = Number.isFinite(savingsAmount) && savingsAmount > 0 && Number.isFinite(savingsPercent);
  const hasDifference = Number.isFinite(differenceAmount) && Number.isFinite(differencePercent);
  const absoluteDifferenceAmount = Math.abs(differenceAmount);
  const absoluteDifferencePercent = Math.abs(differencePercent);
  const differenceDirection = differenceAmount > 0 ? 'above' : differenceAmount < 0 ? 'below' : 'from';

  return (
    <article
      id={car?.scrollId}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(car)}
      onKeyDown={handleCardKeyDown}
      aria-label={`${title} listing`}
    >
      <div className="relative">
        <div className="aspect-[4/3] w-full bg-slate-100">
          {!imageError && car.imageUrl ? (
            <img
              src={car.imageUrl}
              alt={`${title} vehicle`}
              loading="lazy"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-100 to-slate-200 px-4 text-center text-slate-500">
              <FaCarSide className="text-3xl text-slate-400" />
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Image unavailable
              </div>
              <div className="text-sm text-slate-600">
                Listing photo not provided by the dealer.
              </div>
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-slate-950/35 to-transparent" />

        <div className="absolute left-3 top-3">
          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
            car.priceLabel === 'Great Deal' || car.priceLabel === 'Below estimate'
              ? 'bg-emerald-600 text-white'
              : car.priceLabel === 'Fair Price'
                || car.priceLabel === 'Near estimate'
                ? 'bg-amber-400 text-slate-900'
                : car.priceLabel === 'Above estimate'
                  ? 'bg-rose-600 text-white'
                  : 'bg-slate-200 text-slate-800'
          }`}>
            {car.priceLabel || (hasSavings ? 'Below estimate' : 'Market insight')}
          </span>
        </div>

        <div className="absolute right-3 top-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/90 text-slate-700 shadow-sm backdrop-blur transition hover:bg-white hover:text-rose-600"
            aria-label={isSaved ? 'Unsave car' : 'Save car'}
            title={isSaved ? 'Unsave car' : 'Save car'}
          >
            {isSaved ? <FaHeart className="text-rose-600" /> : <FaRegHeart />}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/90 text-slate-700 shadow-sm backdrop-blur transition hover:bg-white hover:text-teal-700"
            aria-label="Share listing"
            title="Share listing"
          >
            <FaShareAlt />
          </button>
        </div>

        {toast && (
          <div className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-slate-950 px-3 py-1.5 text-xs font-medium text-white shadow-lg">
            {toast}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-slate-950 sm:text-xl">
              {title}
            </h3>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-2xl font-semibold tracking-tight text-slate-950">{fmtPrice(car.price)}</div>
          </div>
        </div>

        <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-3">
          <div className="flex items-center gap-2">
            <FaTachometerAlt className="text-slate-400" />
            <span>{fmtInt(car.mileage)} miles</span>
          </div>
          <div className="flex items-center gap-2">
            <FaMapMarkerAlt className="text-slate-400" />
            <span className="min-w-0 truncate">{locationLabel(car)}</span>
          </div>
          <div className="flex items-center gap-2">
            <FaCarSide className="text-slate-400" />
            <span className="min-w-0 truncate">{car.trim || 'Trim not listed'}</span>
          </div>
        </div>

        {car.dealerId && car.dealerName && (
          <div className="mt-3 text-sm text-slate-600">
            Dealer:{' '}
            <Link
              to={`/dealer/${car.dealerId}`}
              className="font-medium text-teal-700 underline decoration-teal-300 underline-offset-4 hover:text-teal-900"
              onClick={(e) => {
                e.stopPropagation();
                onDealerClick?.(car);
              }}
            >
              {car.dealerName}
            </Link>
          </div>
        )}

        <div className="mt-4">
          {pricePosition && hasDifference ? (
            <div className={`rounded-xl border px-3 py-2.5 ${
              pricePosition === 'below'
                ? 'border-emerald-200 bg-emerald-50'
                : pricePosition === 'above'
                  ? 'border-rose-200 bg-rose-50'
                  : 'border-amber-200 bg-amber-50'
            }`}>
              <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${
                pricePosition === 'below'
                  ? 'text-emerald-700'
                  : pricePosition === 'above'
                    ? 'text-rose-700'
                    : 'text-amber-700'
              }`}>
                Meshum Estimate {fmtPrice(mestimateValue)}
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-950">
                {pricePosition === 'near'
                  ? `Within ${fmtPrice(absoluteDifferenceAmount)} of Meshum Estimate`
                  : `${fmtPrice(absoluteDifferenceAmount)} ${differenceDirection} Meshum Estimate`}
              </div>
              <div className={`mt-1 text-xs font-medium ${
                pricePosition === 'below'
                  ? 'text-emerald-800'
                  : pricePosition === 'above'
                    ? 'text-rose-800'
                    : 'text-amber-800'
              }`}>
                {(absoluteDifferencePercent * 100).toFixed(1)}% {differenceDirection} estimate
                {mestimateCompsCount > 0 ? ` with ${mestimateCompsCount} sold comps` : ''}
              </div>
            </div>
          ) : hasSavings ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                Meshum Estimate {fmtPrice(mestimateValue)}
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-950">
                {fmtPrice(savingsAmount)} below Meshum Estimate
              </div>
              <div className="mt-1 text-xs font-medium text-emerald-800">
                {(savingsPercent * 100).toFixed(1)}% below estimate
                {mestimateCompsCount > 0 ? ` with ${mestimateCompsCount} sold comps` : ''}
              </div>
            </div>
          ) : (
            <MarketInsight
              price={car.price}
              mestimateStatus={mestimateStatus}
              mestimate={mestimateValue}
              compsCount={mestimateCompsCount}
            />
          )}
        </div>

        <div className="mt-auto flex justify-end pt-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(car);
            }}
            className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 sm:w-auto"
          >
            View details
            <FaChevronRight className="text-[0.7rem]" />
          </button>
        </div>
      </div>
    </article>
  );
}

function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const searchParams = new URLSearchParams(location.search);

  const initialMake = searchParams.get('make') || '';
  const initialModel = normalizeModel(searchParams.get('model'));
  const initialRadius = searchParams.get('radius') || '25';
  const initialZip = searchParams.get('zip') || '';
  const initialMinYear = searchParams.get('minYear') || '';
  const initialMaxYear = searchParams.get('maxYear') || '';
  const initialMinPrice = searchParams.get('minPrice') || '';
  const initialMaxPrice = searchParams.get('maxPrice') || '';
  const initialSort = searchParams.get('sort') || '';
  const initialTrim = searchParams.get('trim') || '';
  const initialMaxMileage = searchParams.get('maxMileage') || '';

  const [make, setMake] = useState(initialMake);
  const [model, setModel] = useState(initialModel);
  const [radius, setRadius] = useState(initialRadius);
  const [zip, setZip] = useState(initialZip);
  const [minYear, setMinYear] = useState(initialMinYear);
  const [maxYear, setMaxYear] = useState(initialMaxYear);
  const [minPrice, setMinPrice] = useState(initialMinPrice);
  const [maxPrice, setMaxPrice] = useState(initialMaxPrice);
  const [sort, setSort] = useState(initialSort);
  const [trim, setTrim] = useState(initialTrim);
  const [maxMileage, setMaxMileage] = useState(initialMaxMileage);

  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [trimOptions, setTrimOptions] = useState([]);
  const [cars, setCars] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedCarIds, setSavedCarIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [renderModal, setRenderModal] = useState(false);
  const [filters, setFilters] = useState({
    zip: '',
    makeName: '',
    model: '',
    trim: '',
    minYear: '',
    maxYear: '',
    minPrice: '',
    maxPrice: '',
    maxDistance: '',
    maxMileage: '',
    sort: '',
  });

  const { user } = useAuth();
  const modalRef = useRef(null);
  const openBtnRef = useRef(null);
  const restoreTimerRef = useRef(null);
  const trackedResultsQueryRef = useRef(null);
  const TRANSITION_MS = 180;
  const currentPage = parseInt(new URLSearchParams(location.search).get('page') || '1', 10);

  const onOverlayMouseDown = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setShowFilterModal(false);
    }
  };

  const updateSearchParams = (updates = {}, removals = []) => {
    const params = new URLSearchParams(location.search);
    removals.forEach((key) => params.delete(key));
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    params.set('page', '1');
    navigate(`/search?${params.toString()}`);
  };

  const clearRefinements = () => {
    setMinYear('');
    setMaxYear('');
    setMinPrice('');
    setMaxPrice('');
    setTrim('');
    setMaxMileage('');
    setSort('');
    updateSearchParams({}, ['trim', 'minYear', 'maxYear', 'minPrice', 'maxPrice', 'maxMileage', 'sort']);
  };

  const handleFilterChipRemove = (key) => {
    const nextState = {
      make: setMake,
      model: setModel,
      trim: setTrim,
      minYear: setMinYear,
      maxYear: setMaxYear,
      minPrice: setMinPrice,
      maxPrice: setMaxPrice,
      maxMileage: setMaxMileage,
      sort: setSort,
    };
    nextState[key]?.('');
    const removals = [key];

    if (key === 'make') {
      setModel('');
      setTrim('');
      removals.push('model', 'trim');
    }

    if (key === 'model') {
      setTrim('');
      removals.push('trim');
    }

    if (key === 'zip' || key === 'radius') {
      return;
    }

    updateSearchParams({}, removals);
  };

  const activeChips = [
    make && { key: 'make', label: `Make: ${make}` },
    model && { key: 'model', label: `Model: ${model}` },
    trim && { key: 'trim', label: `Trim: ${trim}` },
    minYear && { key: 'minYear', label: `Min year: ${minYear}` },
    maxYear && { key: 'maxYear', label: `Max year: ${maxYear}` },
    minPrice && { key: 'minPrice', label: `Min price: ${fmtPrice(minPrice)}` },
    maxPrice && { key: 'maxPrice', label: `Max price: ${fmtPrice(maxPrice)}` },
    maxMileage && { key: 'maxMileage', label: `Max mileage: ${fmtInt(maxMileage)}` },
    sort && { key: 'sort', label: `Sort: ${SORT_LABELS[sort] || sort}` },
  ].filter(Boolean);

  const searchContextLabel = zip
    ? `Search near ${zip}${radius ? ` within ${radius === '9999' ? 'nationwide' : `${radius} miles`}` : ''}`
    : radius
      ? `${radius === '9999' ? 'Nationwide search' : `${radius} mile search radius`}`
      : '';

  const pageTitle = make && model
    ? `${make} ${model} for sale`
    : make
      ? `${make} vehicles for sale`
      : 'Used cars for sale';

  const pageDescription = make && model
    ? `Compare active ${make} ${model} listings with sold-vehicle pricing and dealer details.`
    : make
      ? `See active ${make} listings alongside market-value clues and dealer details.`
      : 'Compare live listings with sold-price context to judge value faster.';
  const searchCanonicalUrl = 'https://meshum.com/search';

  useEffect(() => {
    const url = `${location.pathname}${location.search}`;
    localStorage.setItem('lastSearchURL', url);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!user?.id) return;

    fetch(`${process.env.REACT_APP_API_BASE}/saved/${user.id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setSavedCarIds(data))
      .catch((err) => console.error('Failed to fetch saved cars:', err));
  }, [user]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE}/cars/makes`)
      .then((res) => res.json())
      .then((data) => setMakeOptions(data));
  }, []);

  useEffect(() => {
    setTrim('');
  }, [make, model]);

  useEffect(() => {
    if (make) {
      fetch(`${process.env.REACT_APP_API_BASE}/cars/models?make=${encodeURIComponent(make)}`)
        .then((res) => res.json())
        .then((data) => setModelOptions(data));
    } else {
      setModelOptions([]);
    }
  }, [make]);

  useEffect(() => {
    if (restoreTimerRef.current) {
      window.clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = null;
    }

    const cachedCars = sessionStorage.getItem('cachedCars');
    const cachedPage = sessionStorage.getItem('cachedPage');
    const cachedQuery = sessionStorage.getItem('cachedQuery');
    const page = new URLSearchParams(location.search).get('page') || '1';
    const query = location.search;

    if (cachedCars && cachedPage === page && cachedQuery === query) {
      setCars(JSON.parse(cachedCars));
      setTotalPages(parseInt(sessionStorage.getItem('cachedTotalPages') || '1', 10));
      setTotalResults(parseInt(sessionStorage.getItem('cachedTotalResults') || '0', 10));
      setLoading(false);

      restoreTimerRef.current = window.setTimeout(() => {
        const carId = sessionStorage.getItem('carScrollId');
        if (carId) {
          const el = document.getElementById(`car-${carId}`);
          if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
          sessionStorage.removeItem('carScrollId');
        } else {
          const scrollY = parseInt(sessionStorage.getItem('scrollPos') || '0', 10);
          window.scrollTo(0, scrollY);
        }
        restoreTimerRef.current = null;
      }, 0);

      return () => {
        if (restoreTimerRef.current) {
          window.clearTimeout(restoreTimerRef.current);
          restoreTimerRef.current = null;
        }
      };
    }

    const params = new URLSearchParams(location.search);
    setLoading(true);

    fetch(`${process.env.REACT_APP_API_BASE}/cars/active?${params.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error('Search request failed');
        return res.json();
      })
      .then((data) => {
        const resultCount = Number(data?.totalResults || 0);
        setCars(data?.results || []);
        setTotalPages(data?.totalPages || 1);
        setTotalResults(resultCount);
        sessionStorage.setItem('cachedCars', JSON.stringify(data?.results || []));
        sessionStorage.setItem('cachedTotalPages', data?.totalPages?.toString() || '1');
        sessionStorage.setItem('cachedTotalResults', data?.totalResults?.toString() || '0');
        sessionStorage.setItem('cachedPage', page);
        sessionStorage.setItem('cachedQuery', query);
        sessionStorage.setItem('cachedScroll', '0');
        if (trackedResultsQueryRef.current !== query) {
          trackedResultsQueryRef.current = query;
          window.gtag?.('event', 'search_results_loaded', {
            results_found: resultCount > 0,
            result_count: resultCount,
            make: params.get('make') || '',
            model: normalizeModel(params.get('model')),
            radius: params.get('radius') || '25',
            page: Number(page),
          });
        }
      })
      .catch(() => {
        setCars([]);
        setTotalPages(1);
        setTotalResults(0);
      })
      .finally(() => setLoading(false));
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setMake(params.get('make') || '');
    setModel(normalizeModel(params.get('model')));
    setRadius(params.get('radius') || '25');
    setZip(params.get('zip') || '');
    setMinYear(params.get('minYear') || '');
    setMaxYear(params.get('maxYear') || '');
    setMinPrice(params.get('minPrice') || '');
    setMaxPrice(params.get('maxPrice') || '');
    setSort(params.get('sort') || '');
    setTrim(params.get('trim') || '');
    setMaxMileage(params.get('maxMileage') || '');
  }, [location.search]);

  useEffect(() => {
    if (make && model) {
      fetch(`${process.env.REACT_APP_API_BASE}/cars/trims?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`)
        .then((res) => res.json())
        .then((data) => setTrimOptions(data))
        .catch(() => setTrimOptions([]));
    } else {
      setTrimOptions([]);
    }
  }, [make, model]);

  useEffect(() => {
    if (navigationType === 'POP') return;
    if (sessionStorage.getItem('carScrollId')) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.search, navigationType]);

  useEffect(() => {
    if (!showFilterModal) {
      openBtnRef.current?.focus();
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusableSel = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const modalEl = modalRef.current;
    let focusables = [];

    if (modalEl) {
      focusables = Array.from(modalEl.querySelectorAll(focusableSel));
      setTimeout(() => {
        (focusables[0] || modalEl).focus();
      }, 0);
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowFilterModal(false);
        return;
      }

      if (e.key === 'Tab' && focusables.length) {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showFilterModal]);

  useEffect(() => {
    if (showFilterModal) {
      setRenderModal(true);
      return;
    }

    const t = setTimeout(() => setRenderModal(false), TRANSITION_MS);
    return () => clearTimeout(t);
  }, [showFilterModal]);

  const handleSearchClick = () => {
    const query = new URLSearchParams();
    if (make) query.append('make', make);
    if (model) query.append('model', model);
    if (radius) query.append('radius', radius);
    if (zip) query.append('zip', zip);
    if (minYear) query.append('minYear', minYear);
    if (maxYear) query.append('maxYear', maxYear);
    if (minPrice) query.append('minPrice', minPrice);
    if (maxPrice) query.append('maxPrice', maxPrice);
    if (sort) query.append('sort', sort);
    if (trim) query.append('trim', trim);
    if (maxMileage) query.append('maxMileage', maxMileage);

    query.append('page', '1');
    sessionStorage.setItem('cachedCars', JSON.stringify([]));
    sessionStorage.setItem('cachedTotalPages', '0');
    sessionStorage.setItem('cachedTotalResults', '0');
    sessionStorage.setItem('cachedPage', '');
    sessionStorage.setItem('cachedScroll', '0');
    sessionStorage.removeItem('cachedQuery');
    sessionStorage.removeItem('carScrollId');
    window.gtag?.('event', 'search_submit', {
      make,
      model,
      radius,
      minYear,
      maxYear,
      minPrice,
      maxPrice,
      maxMileage,
    });
    navigate(`/search?${query.toString()}`);
  };

  const handleCarClick = (car) => {
    sessionStorage.setItem('cachedCars', JSON.stringify(cars));
    sessionStorage.setItem('cachedPage', currentPage.toString());
    sessionStorage.setItem('scrollPos', window.scrollY);
    sessionStorage.setItem('carScrollId', car.id);
    navigate(getVehiclePath(car), {
      state: {
        from: { pathname: location.pathname, search: location.search },
      },
    });
  };

  const handleToggleSavedCar = async (carId, currentlySaved) => {
    if (!user?.id) return { ok: false, reason: 'auth' };

    try {
      const method = currentlySaved ? 'DELETE' : 'POST';
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/saved`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, carId }),
      });

      if (!res.ok) return { ok: false };

      setSavedCarIds((prev) => {
        if (currentlySaved) return prev.filter((id) => id !== carId);
        return Array.from(new Set([...prev, carId]));
      });

      return { ok: true };
    } catch (err) {
      console.error('Error toggling saved state:', err);
      return { ok: false };
    }
  };

  const goToPage = (newPage) => {
    const params = new URLSearchParams(location.search);
    params.set('page', newPage);
    sessionStorage.removeItem('carScrollId');
    navigate(`/search?${params.toString()}`);
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle} | Meshum</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={searchCanonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content="Compare live listings with sold prices and dealer details." />
        <meta property="og:url" content={searchCanonicalUrl} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content={cars.length > 0 ? cars[0].primaryImageUrl : 'https://meshum.com/share-thumbnail.jpg'}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content="Compare live listings with sold-price insight before you buy." />
        <meta
          name="twitter:image"
          content={cars.length > 0 ? cars[0].primaryImageUrl : 'https://meshum.com/share-thumbnail.jpg'}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            name: pageTitle,
            url: searchCanonicalUrl,
          })}
        </script>
      </Helmet>

      <main className="ui-page bg-slate-50">
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 max-w-3xl">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
                Search results
              </div>
              <h1 className="mt-2 text-2xl font-semibold leading-tight text-slate-950 sm:text-3xl">
                {pageTitle}
              </h1>
              <p className="mt-2 hidden text-sm leading-6 text-slate-600 sm:block sm:text-base">
                {pageDescription}
              </p>
              {searchContextLabel && (
                <div className="mt-3 text-sm font-medium text-slate-600 sm:hidden">
                  {searchContextLabel}
                </div>
              )}
            </div>

            <div className="w-full lg:max-w-2xl">
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                <label className="block">
                  <span className="ui-field-label">Sort</span>
                  <select
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value);
                      updateSearchParams({ sort: e.target.value });
                    }}
                    className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">Sort by</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="year_desc">Year: Newest</option>
                    <option value="year_asc">Year: Oldest</option>
                    <option value="mileage_asc">Mileage: Low to High</option>
                  </select>
                </label>

                <div className="flex flex-wrap gap-2 sm:items-end sm:justify-end">
                  <button
                    ref={openBtnRef}
                    onClick={() => setShowFilterModal(true)}
                    className="btn-secondary h-11 px-3 text-sm sm:h-auto sm:px-4"
                    type="button"
                  >
                    <FaFilter className="mr-2" />
                    Filters
                  </button>
                  <button
                    onClick={() => {
                      if (user?.id) {
                        window.gtag?.('event', 'save_search', {
                          event_category: 'engagement',
                          make: make || '',
                          model: model || '',
                          radius: radius || '25',
                        });

                        setFilters({
                          zip,
                          makeName: make,
                          model,
                          trim,
                          minYear,
                          maxYear,
                          minPrice,
                          maxPrice,
                          maxDistance: radius,
                          maxMileage,
                          sort,
                        });
                        setShowModal(true);
                      } else {
                        window.gtag?.('event', 'save_search_blocked', {
                          event_category: 'engagement',
                          event_label: 'Guest tried to save search',
                        });

                        const msg = document.getElementById('save-search-tooltip');
                        if (msg) {
                          msg.style.opacity = 1;
                          msg.style.visibility = 'visible';
                          setTimeout(() => {
                            msg.style.opacity = 0;
                            msg.style.visibility = 'hidden';
                          }, 2000);
                        }
                      }
                    }}
                    className="btn-secondary h-11 px-3 text-sm sm:h-auto sm:px-4"
                    type="button"
                  >
                    <span className="hidden sm:inline">Save search</span>
                    <span className="sm:hidden">Save</span>
                  </button>
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      navigator.clipboard.writeText(url);
                      const tip = document.getElementById('share-tooltip');
                      if (tip) {
                        tip.style.opacity = 1;
                        tip.style.visibility = 'visible';
                        setTimeout(() => {
                          tip.style.opacity = 0;
                          tip.style.visibility = 'hidden';
                        }, 2000);
                      }
                    }}
                    className="btn-secondary h-11 px-3 text-sm sm:h-auto sm:px-4"
                    type="button"
                    aria-label="Share this search"
                  >
                    <FaShareAlt className="mr-2" />
                    <span className="hidden sm:inline">Share</span>
                  </button>
                </div>
              </div>

              <div className="relative mt-2 h-0">
                <div
                  id="save-search-tooltip"
                  className="invisible absolute right-0 top-0 z-10 -translate-y-2 rounded-full bg-slate-950 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity"
                >
                  Please log in to save searches
                </div>
                <div
                  id="share-tooltip"
                  className="invisible absolute right-24 top-0 z-10 -translate-y-2 rounded-full bg-slate-950 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity"
                >
                  Link copied
                </div>
              </div>

              {searchContextLabel && (
                <div className="mt-4 hidden flex-wrap gap-2 text-sm text-slate-600 sm:flex">
                  {zip && (
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium">
                      ZIP: {zip}
                    </span>
                  )}
                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium">
                    {radius === '9999' ? 'Nationwide' : `${radius} mi`}
                  </span>
                  <span className="inline-flex items-center py-1.5 font-medium text-slate-600">
                    {searchContextLabel}
                  </span>
                </div>
              )}

              <div className="mt-4 hidden flex-wrap gap-2 sm:flex">
                {activeChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => handleFilterChipRemove(chip.key)}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    <span>{chip.label}</span>
                    <FaTimes className="text-[0.65rem] text-slate-400" />
                  </button>
                  ))}
              </div>

              <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-slate-600">
                  {loading
                    ? 'Loading listings...'
                    : cars.length > 0
                      ? `Showing ${(currentPage - 1) * 20 + 1}-${(currentPage - 1) * 20 + cars.length} of ${totalResults} ${totalResults === 1 ? 'car' : 'cars'}`
                      : `Showing 0 of ${totalResults} cars`}
                </div>

                {user?.id && (
                  <Link
                    to="/savedsearches"
                    className="text-sm font-medium text-teal-700 underline decoration-teal-300 underline-offset-4 hover:text-teal-900"
                  >
                    View saved searches
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        {renderModal && (
          <div
            className="ui-modal-overlay items-start sm:items-center"
            style={{
              transition: `opacity ${TRANSITION_MS}ms ease`,
              opacity: showFilterModal ? 1 : 0,
              pointerEvents: showFilterModal ? 'auto' : 'none',
            }}
            onMouseDown={onOverlayMouseDown}
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
            aria-hidden={showFilterModal ? 'false' : 'true'}
          >
            <div
              ref={modalRef}
              tabIndex={-1}
              className="ui-modal max-h-[calc(100vh-1.5rem)] max-w-2xl overflow-y-auto"
              style={{
                transition: `transform ${TRANSITION_MS}ms ease, opacity ${TRANSITION_MS}ms ease`,
                transform: showFilterModal ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.98)',
                opacity: showFilterModal ? 1 : 0,
                willChange: 'transform, opacity',
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-slate-950">Refine results</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Narrow the market without losing your search context.
                  </p>
                </div>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="btn-icon"
                  aria-label="Close filters"
                  type="button"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="ui-field-label">ZIP</label>
                  <input
                    placeholder="ZIP"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                  />
                </div>

                <div>
                  <label className="ui-field-label">Radius</label>
                  <select value={radius} onChange={(e) => setRadius(e.target.value)}>
                    <option value="5">5 miles</option>
                    <option value="10">10 miles</option>
                    <option value="25">25 miles</option>
                    <option value="50">50 miles</option>
                    <option value="100">100 miles</option>
                    <option value="250">250 miles</option>
                    <option value="9999">Nationwide</option>
                  </select>
                </div>

                <div>
                  <label className="ui-field-label">Make</label>
                  <select
                    value={make}
                    onChange={(e) => {
                      setMake(e.target.value);
                      setModel('');
                    }}
                  >
                    <option value="">All makes</option>
                    {makeOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ui-field-label">Model</label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    disabled={!make}
                  >
                    <option value="">All models</option>
                    {modelOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ui-field-label">Trim</label>
                  <select value={trim} onChange={(e) => setTrim(e.target.value)}>
                    <option value="">All trims</option>
                    {trimOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ui-field-label">Max mileage</label>
                  <select value={maxMileage} onChange={(e) => setMaxMileage(e.target.value)}>
                    <option value="">Any mileage</option>
                    <option value="15000">Less than 15,000 miles</option>
                    <option value="30000">Less than 30,000 miles</option>
                    <option value="45000">Less than 45,000 miles</option>
                    <option value="60000">Less than 60,000 miles</option>
                    <option value="80000">Less than 80,000 miles</option>
                  </select>
                </div>

                <div>
                  <label className="ui-field-label">Min year</label>
                  <input
                    type="number"
                    placeholder="Min year"
                    value={minYear}
                    onChange={(e) => setMinYear(e.target.value)}
                  />
                </div>

                <div>
                  <label className="ui-field-label">Max year</label>
                  <input
                    type="number"
                    placeholder="Max year"
                    value={maxYear}
                    onChange={(e) => setMaxYear(e.target.value)}
                  />
                </div>

                <div>
                  <label className="ui-field-label">Min price</label>
                  <input
                    type="number"
                    placeholder="Min price"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                  />
                </div>

                <div>
                  <label className="ui-field-label">Max price</label>
                  <input
                    type="number"
                    placeholder="Max price"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  onClick={() => setShowFilterModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={clearRefinements}
                  className="btn-secondary"
                >
                  Clear refinements
                </button>

                <button
                  type="button"
                  onClick={() => {
                    localStorage.setItem('lastFilters', JSON.stringify({
                      zip,
                      radius,
                      make,
                      model,
                      trim,
                      minYear,
                      maxYear,
                      minPrice,
                      maxPrice,
                      maxMileage,
                      sort,
                    }));
                    handleSearchClick();
                    setShowFilterModal(false);
                  }}
                  className="btn-primary"
                  disabled={!make}
                >
                  Apply filters
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <Spinner />
        ) : (
          <>
            {cars.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
                <h3 className="text-lg font-semibold text-slate-950">No cars found</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Try widening the radius or adjusting filters.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <button type="button" onClick={() => setShowFilterModal(true)} className="btn-primary">
                    Open filters
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {cars.map((car) => (
                  <SearchResultCard
                    key={car.id}
                    car={{ ...car, scrollId: `car-${car.id}` }}
                    saved={savedCarIds.includes(car.id)}
                    userId={user?.id}
                    onOpen={handleCarClick}
                    onToggleSaved={handleToggleSavedCar}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {totalPages > 1 && !loading && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-center">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="btn-primary bg-teal-600 hover:bg-teal-700"
              type="button"
            >
              Prev
            </button>
            <span className="text-sm font-semibold text-slate-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="btn-primary bg-teal-600 hover:bg-teal-700"
              type="button"
            >
              Next
            </button>
          </div>
        )}

        <RecentlyViewed cars={JSON.parse(localStorage.getItem('recentlyViewedCars') || '[]')} />

        <SaveSearchModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSaved={() => {}}
          currentFilters={filters}
          userId={user?.id}
        />
      </main>
    </>
  );
}

export default SearchResults;
